'use client';
import { cn } from '@/components/ui/cn';
import { Box, Stack } from '@/components/ui/layout';
import layoutStyles from './reset-password-form.layout.module.css';

import Button from '@/components/app/Button';
import { Alert } from '@/components/ui/alert';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { linkClasses } from '@/components/ui/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';

import { logError } from '@/utils/error-handler';
import { useSendPasswordResetEmailMutation } from '../api/send-password-reset-email';

const resetPasswordSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email(),
});

export const ResetPasswordForm = () => {
  const [emailSentTo, setEmailSentTo] = useState('');

  const { mutate: sendPasswordResetEmailMutate, isPending } =
    useSendPasswordResetEmailMutation();

  const { control, handleSubmit, formState, setError } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = handleSubmit(({ email }) => {
    sendPasswordResetEmailMutate(email, {
      onSuccess: (email) => {
        setEmailSentTo(email);
      },
      onError: (err) => {
        setError('root.globalError', {
          message:
            'There was an issue encountered while attempting to reset your password.',
        });
        logError(err, { email });
      },
    });
  });

  const isSent = emailSentTo !== '';

  return (
    <Box as="form" onSubmit={onSubmit} className={cn(layoutStyles.layout1)}>
      <Stack className={cn(layoutStyles.layout2)}>
        <h1 className="font-semibold leading-relaxed">Forgot your password?</h1>
        <div className="max-w-112 text-md">
          Enter your account email and we&apos;ll send a secure reset link that
          stays active for 2 hours.
        </div>
      </Stack>
      {!isSent && (
        <Controller
          control={control}
          name="email"
          render={({
            field: { onChange, onBlur, value },
            fieldState: { error },
          }) => (
            /*
             * Census sweep: the house `Field` owns the label/message rhythm
             * (the MUI field's floated label reserved no layout space), and
             * the `data-testid` rides the control natively instead of MUI's
             * `inputProps` bucket.
             */
            <Field
              disabled={formState.isSubmitting}
              error={Boolean(error)}
              helperText={formState.errors?.email?.message}
              label="Email"
            >
              <Input
                data-testid="reset-password-email-input"
                onBlur={onBlur}
                onChange={onChange}
                type="email"
                value={value}
              />
            </Field>
          )}
        />
      )}

      {formState.errors.root?.globalError && (
        <Alert className="mb-8" severity="error">
          {formState.errors.root.globalError.message}
        </Alert>
      )}

      {isSent && (
        <Alert
          data-testid="reset-password-success-alert"
          className="mb-8"
          severity="success"
        >
          If an account with an email of <strong>{emailSentTo}</strong> exists,
          you will receive an email with instructions on how to reset your
          password.
        </Alert>
      )}

      <Stack className={cn(layoutStyles.layout3)}>
        {!isSent && (
          <Button
            onClick={onSubmit}
            size="large"
            loading={formState.isSubmitting || isPending}
            loadingText="Sending reset email..."
            type="submit"
            data-testid="reset-password-btn"
          >
            Send reset email
          </Button>
        )}
        <RouterLink className={linkClasses()} data-testid="sign-in-btn" to="/">
          {emailSentTo !== '' ? 'Back to login' : 'Sign In'}
        </RouterLink>
      </Stack>
    </Box>
  );
};
